import { CheckIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Group,
  List,
  Loader,
  rem,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { CVItemCard } from '../../../../../components/common/CVItemCard';
import { CVSectionContainer } from '../../../../../components/common/CVSectionContainer';
import { DeleteConfirmationModal } from '../../../../../components/common/DeleteConfirmationModal';
import { MonthYearInput, validateMonthYear } from '../../../../../components/common/MonthYearInput';
import { ValidationResult } from '../../../../../components/common/ValidationResult';
import { FormUIColor } from '../../../../../constants/colorPalette';
import { useEditMode } from "../../../../../context/EditModeContext";
import { useEducationDegreesDictionary, useEducationInstitutionsDictionary } from '../../../../../hooks/queries/useEducationDictionaries';
import { EducationService, PersonEducationModel } from '../../../../../services/educationService';
import { Education as EducationType } from '../../../../../types/cv';
import { ValidateableCvSections } from '../../../../../types/cvSections';
import { createDateString, formatDateRange, normalizeYearInput, parseDateString, toUTCDate } from '../../../../../utils/dateFormat';

const INSTRUCTIONS = (
  <Stack gap={5}>
    <Text fw={500}>Instructions from your company</Text>
    <Text size="sm">Provide your educational background (at least your highest level of education):</Text>
    <Text size="sm" mt={5}>- Start and end year</Text>
    <Text size="sm">- Name of the school or institution</Text>
    <Text size="sm">- Level of attainment (if not a full degree, you can include the number of study credits or credits earned)</Text>
    <Text size="sm">- Brief description of the main subjects covered in the studies and possibly the theme of the master's thesis or similar.</Text>
  </Stack>
);

interface EducationProps {
  education: EducationType[];
  onChange: (education: EducationType[]) => void;
  personId?: number;
  cvId?: number;
}

function getEducationValidationMessage(error: unknown): string | null {
  const err = error as Error & { responseBody?: { errors?: Record<string, string[]>; title?: string } };
  const body = err?.responseBody;
  if (!body) return null;
  if (body.errors) {
    const firstMessages = Object.values(body.errors)[0];
    if (firstMessages?.length) return firstMessages[0];
  }
  return body.title ?? null;
}

export function Education({ education, onChange, personId, cvId }: EducationProps) {
  const { isEditMode } = useEditMode();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingEducationId, setEditingEducationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Function to format education data for LLM validation
  const getEducationSectionData = (): string => {
    if (!education || education.length === 0) {
      return 'No education entries found.';
    }

    return education.map((edu, index) => {
      const dateRange = formatDateRange(edu.fromDate, edu.toDate);
      return `
Education ${index + 1}:
- Period: ${dateRange}
- Institution: ${edu.school}
- Degree: ${edu.degree}
- Description: ${edu.description || 'No description provided'}
      `.trim();
    }).join('\n\n');
  };
  const [newEducation, setNewEducation] = useState<Partial<EducationType> & {
    fromMonth: string;
    fromYear: string;
    toMonth: string;
    toYear: string;
  }>({
    school: '',
    degree: '',
    description: '',
    fromMonth: '',
    fromYear: '',
    toMonth: '',
    toYear: ''
  });

  const [editEducation, setEditEducation] = useState<Partial<EducationType> & {
    fromMonth: string;
    fromYear: string;
    toMonth: string;
    toYear: string;
  }>({
    school: '',
    degree: '',
    description: '',
    fromMonth: '',
    fromYear: '',
    toMonth: '',
    toYear: ''
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [educationToDelete, setEducationToDelete] = useState<EducationType | null>(null);
  const [showAddValidation, setShowAddValidation] = useState(false);
  const [showEditValidation, setShowEditValidation] = useState(false);
  const { data: degreesDictionaryData, isLoading: isLoadingDegrees } = useEducationDegreesDictionary();
  const { data: institutionsDictionaryData, isLoading: isLoadingInstitutions } = useEducationInstitutionsDictionary();
  const degreesDictionary = degreesDictionaryData?.filter((n): n is string => !!n) ?? [];
  const institutionsDictionary = institutionsDictionaryData?.filter((n): n is string => !!n) ?? [];
  const educationService = new EducationService();

  useEffect(() => {
    if (personId && isEditMode) {
      loadEducation();
    }
  }, [personId, isEditMode]);

  // Close and reset all forms when edit mode is disabled
  useEffect(() => {
    if (!isEditMode) {
      setIsAddFormOpen(false);
      setEditingEducationId(null);
      setNewEducation({
        school: '',
        degree: '',
        description: '',
        fromMonth: '',
        fromYear: '',
        toMonth: '',
        toYear: ''
      });
      setShowAddValidation(false);
      setShowEditValidation(false);
      setDeleteModalOpen(false);
    }
  }, [isEditMode]);

  const loadEducation = async () => {
    if (!personId) return;

    setIsLoading(true);
    try {
      const data = await educationService.getEducationListByPersonId(personId);

      const transformedEducation: EducationType[] = data.map(item => {
        // Use UTC methods to avoid timezone issues
        const fromDate = new Date(item.dateStart);
        const fromYear = fromDate.getUTCFullYear();
        const fromMonth = (fromDate.getUTCMonth() + 1).toString().padStart(2, '0');

        let toDateString = '';
        if (item.dateEnd) {
          const toDate = new Date(item.dateEnd);
          const toYear = toDate.getUTCFullYear();
          const toMonth = (toDate.getUTCMonth() + 1).toString().padStart(2, '0');
          toDateString = `${toYear}-${toMonth}`;
        }

        return {
          id: item.id.toString(),
          fromDate: `${fromYear}-${fromMonth}`,
          toDate: toDateString,
          school: item.institution?.name || '',
          degree: item.degree?.name || '',
          description: item.faculty?.name || ''
        };
      });

      onChange(transformedEducation);
    } catch (error) {
      console.error('Error loading education:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load education data. Please try again.',
        color: 'red'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEducation = async () => {
    setShowAddValidation(true);

    if (!newEducation.school || !newEducation.degree) {
      notifications.show({
        title: 'Missing Information',
        message: 'Please fill in all required fields',
        color: 'red'
      });
      return;
    }

    // Validate from date using the helper function
    const fromDateError = validateMonthYear(
      newEducation.fromMonth,
      newEducation.fromYear,
      true, // required
      1950,
      new Date().getFullYear()
    );

    if (fromDateError) {
      notifications.show({
        title: 'Invalid Start Date',
        message: fromDateError,
        color: 'red'
      });
      return;
    }

    const normalizedFromYear = normalizeYearInput(newEducation.fromYear);
    const normalizedToYear = newEducation.toYear ? normalizeYearInput(newEducation.toYear) : '';

    if (newEducation.toMonth || newEducation.toYear) {
      const toDateError = validateMonthYear(
        newEducation.toMonth,
        newEducation.toYear,
        false, // not required, but if one is filled, both must be
        1950,
        new Date().getFullYear()
      );

      if (toDateError) {
        notifications.show({
          title: 'Invalid End Date',
          message: toDateError,
          color: 'red'
        });
        return;
      }

      if (newEducation.toMonth && normalizedToYear && newEducation.fromMonth && normalizedFromYear) {
        const fromYear = parseInt(normalizedFromYear);
        const fromMonth = parseInt(newEducation.fromMonth);
        const toYear = parseInt(normalizedToYear);
        const toMonth = parseInt(newEducation.toMonth);

        if (toYear < fromYear || (toYear === fromYear && toMonth < fromMonth)) {
          notifications.show({
            title: 'Invalid Date Range',
            message: 'End date cannot be before start date',
            color: 'red'
          });
          return;
        }
      }
    }

    const educationEntry: EducationType = {
      id: Date.now().toString(),
      fromDate: createDateString(newEducation.fromMonth, normalizedFromYear),
      toDate: newEducation.toMonth && normalizedToYear
        ? createDateString(newEducation.toMonth, normalizedToYear)
        : '',
      school: newEducation.school,
      degree: newEducation.degree,
      description: newEducation.description || ''
    };

    if (personId) {
      setIsSaving(true);
      try {
        // Use UTC to avoid timezone issues
        const fromDate = toUTCDate(new Date(parseInt(normalizedFromYear), parseInt(newEducation.fromMonth) - 1, 1));
        let toDate: Date | null = null;

        if (newEducation.toMonth && normalizedToYear) {
          toDate = toUTCDate(new Date(parseInt(normalizedToYear), parseInt(newEducation.toMonth) - 1, 1));
        }

        const personEducationModel = {
          id: 0,
          // Backend contract uses legacy field name userId, but value is personId.
          userId: personId,
          dateStart: fromDate.toISOString(),
          dateEnd: toDate ? toDate.toISOString() : undefined,
          degree: {
            id: 0,
            name: newEducation.degree,
          },
          institution: {
            id: 0,
            name: newEducation.school,
          },
          faculty: newEducation.description ? {
            id: 0,
            name: newEducation.description,
          } : undefined,
        };

        const result = await educationService.addEducationItem(personEducationModel);

        educationEntry.id = result.id.toString();

        onChange([...education, educationEntry]);
        notifications.show({
          title: 'Success',
          message: 'Education entry added successfully',
          color: 'green'
        });
      } catch (error) {
        console.error('Error adding education:', error);
        const validationMessage = getEducationValidationMessage(error);
        notifications.show({
          title: 'Error',
          message: validationMessage ?? 'Failed to add education entry. Please try again.',
          color: 'red'
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      onChange([...education, educationEntry]);
    }

    setNewEducation({
      school: '',
      degree: '',
      description: '',
      fromMonth: '',
      fromYear: '',
      toMonth: '',
      toYear: ''
    });
    setShowAddValidation(false);
    setIsAddFormOpen(false);
  };

  const handleRemoveEducation = async (id: string) => {
    if (personId) {
      setIsSaving(true);
      try {
        await educationService.deleteEducationItem(parseInt(id));

        onChange(education.filter(edu => edu.id !== id));
        notifications.show({
          title: 'Success',
          message: 'Education entry deleted successfully',
          color: 'green'
        });
      } catch (error) {
        console.error('Error deleting education:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to delete education entry. Please try again.',
          color: 'red'
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // Just update local state if personId is not provided (fallback)
      onChange(education.filter(edu => edu.id !== id));
    }
  };

  const parseDate = (dateString: string): Date => {
    if (!dateString) {
      throw new Error('Date string is required');
    }

    // Handle YYYY-MM format (current utility format)
    if (dateString.includes('-')) {
      const [year, month] = dateString.split('-');
      const parsedYear = parseInt(year);
      const parsedMonth = parseInt(month);

      if (isNaN(parsedMonth) || isNaN(parsedYear) || parsedMonth < 1 || parsedMonth > 12) {
        throw new Error(`Invalid date components: ${dateString}`);
      }

      // Use UTC to avoid timezone issues
      return new Date(Date.UTC(parsedYear, parsedMonth - 1, 1));
    }

    // Handle MM/YYYY format (legacy format)
    if (dateString.includes('/')) {
      const [month, year] = dateString.split('/');
      const parsedMonth = parseInt(month);
      const parsedYear = parseInt(year);

      if (isNaN(parsedMonth) || isNaN(parsedYear) || parsedMonth < 1 || parsedMonth > 12) {
        throw new Error(`Invalid date components: ${dateString}`);
      }

      // Use UTC to avoid timezone issues
      return toUTCDate(new Date(parsedYear, parsedMonth - 1, 1));
    }

    throw new Error(`Unsupported date format: ${dateString}`);
  };

  const handleEditEducation = (edu: EducationType) => {
    const { month: fromMonth, year: fromYear } = parseDateString(edu.fromDate);
    let toMonth = '', toYear = '';

    if (edu.toDate) {
      const parsed = parseDateString(edu.toDate);
      toMonth = parsed.month;
      toYear = parsed.year;
    }

    setEditingEducationId(edu.id);
    setEditEducation({
      fromDate: edu.fromDate,
      toDate: edu.toDate,
      school: edu.school,
      degree: edu.degree,
      description: edu.description,
      fromMonth,
      fromYear,
      toMonth,
      toYear
    });
  };

  const saveEducationEdit = async () => {
    if (!editingEducationId) return;

    setShowEditValidation(true);

    if (!editEducation.school || !editEducation.degree) {
      notifications.show({
        title: 'Missing Information',
        message: 'Please fill in all required fields',
        color: 'red'
      });
      return;
    }

    // Validate from date using the helper function
    const fromDateError = validateMonthYear(
      editEducation.fromMonth,
      editEducation.fromYear,
      true, // required
      1950,
      new Date().getFullYear()
    );

    if (fromDateError) {
      notifications.show({
        title: 'Invalid Start Date',
        message: fromDateError,
        color: 'red'
      });
      return;
    }

    const normalizedFromYear = normalizeYearInput(editEducation.fromYear);
    const normalizedToYear = editEducation.toYear ? normalizeYearInput(editEducation.toYear) : '';

    if (editEducation.toMonth || editEducation.toYear) {
      const toDateError = validateMonthYear(
        editEducation.toMonth,
        editEducation.toYear,
        false, // not required, but if one is filled, both must be
        1950,
        new Date().getFullYear()
      );

      if (toDateError) {
        notifications.show({
          title: 'Invalid End Date',
          message: toDateError,
          color: 'red'
        });
        return;
      }

      if (editEducation.toMonth && normalizedToYear && editEducation.fromMonth && normalizedFromYear) {
        const fromYear = parseInt(normalizedFromYear);
        const fromMonth = parseInt(editEducation.fromMonth);
        const toYear = parseInt(normalizedToYear);
        const toMonth = parseInt(editEducation.toMonth);

        if (toYear < fromYear || (toYear === fromYear && toMonth < fromMonth)) {
          notifications.show({
            title: 'Invalid Date Range',
            message: 'End date cannot be before start date',
            color: 'red'
          });
          return;
        }
      }
    }

    const updatedEducation: EducationType = {
      id: editingEducationId,
      fromDate: createDateString(editEducation.fromMonth, normalizedFromYear),
      toDate: editEducation.toMonth && normalizedToYear
        ? createDateString(editEducation.toMonth, normalizedToYear)
        : '',
      school: editEducation.school,
      degree: editEducation.degree,
      description: editEducation.description || ''
    };

    if (personId) {
      setIsSaving(true);
      try {
        const personEducationModel: PersonEducationModel = {
          id: parseInt(updatedEducation.id),
          // Backend contract uses legacy field name userId, but value is personId.
          userId: personId,
          dateStart: parseDate(updatedEducation.fromDate).toISOString(),
          dateEnd: updatedEducation.toDate && updatedEducation.toDate.trim() !== '' ? parseDate(updatedEducation.toDate).toISOString() : undefined,
          degree: {
            id: 0,
            name: updatedEducation.degree,
          },
          institution: {
            id: 0,
            name: updatedEducation.school,
          },
          faculty: updatedEducation.description ? {
            id: 0,
            name: updatedEducation.description,
          } : undefined,
        };

        await educationService.updateEducationItem(personEducationModel);

        onChange(education.map(edu =>
          edu.id === editingEducationId ? updatedEducation : edu
        ));

        notifications.show({
          title: 'Success',
          message: 'Education entry updated successfully',
          color: 'green'
        });
      } catch (error) {
        console.error('Error updating education:', error);
        const validationMessage = getEducationValidationMessage(error);
        notifications.show({
          title: 'Error',
          message: validationMessage ?? 'Failed to update education entry. Please try again.',
          color: 'red'
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      // Just update local state if personId is not provided (fallback)
      onChange(education.map(edu =>
        edu.id === editingEducationId ? updatedEducation : edu
      ));
    }

    setEditingEducationId(null);
    setShowEditValidation(false);
  };

  const openDeleteConfirmation = (edu: EducationType) => {
    setEducationToDelete(edu);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (educationToDelete) {
      handleRemoveEducation(educationToDelete.id);
      setDeleteModalOpen(false);
    }
  };

  if (isLoading) {
    return (
      <CVSectionContainer
        sectionId="education"
        title="Education"
      >
        <Stack align="center" gap="md" py="xl">
          <Loader size="md" />
          <Text size="sm" c="dimmed">Loading education data...</Text>
        </Stack>
      </CVSectionContainer>
    );
  }

  return (
    <CVSectionContainer
      sectionId="education"
      title="Education"
      buttonText={isEditMode ? "Add Education" : undefined}
      onAddClick={isEditMode ? () => setIsAddFormOpen(true) : undefined}
      instructions={isEditMode ? INSTRUCTIONS : undefined}
      personId={personId}
      cvId={cvId}
      getSectionData={getEducationSectionData}
    >
      <ValidationResult section={ValidateableCvSections.Education} />

      <Collapse in={isAddFormOpen}>
        <Box
          mb="md"
          p="md"
          style={{
            border: `2px solid ${theme.colors.cyan[7]}`,
            borderRadius: theme.radius.sm,
            backgroundColor: isDark ? theme.colors.dark[6] : FormUIColor.ADD_FORM_BG
          }}
        >
          <Stack gap="md">
            <Autocomplete
              label="Degree/Qualification"
              placeholder="e.g. Bachelor of Science in Computer Science"
              required
              value={newEducation.degree}
              onChange={(value) => setNewEducation({ ...newEducation, degree: value })}
              data={degreesDictionary}
              limit={10}
              disabled={isLoadingDegrees}
              rightSection={
                <Tooltip label="Choose from existing degree types or type a new one freely" position="top" multiline maw={220} withArrow>
                  <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                    <InformationCircleIcon style={{ width: rem(16) }} />
                  </ActionIcon>
                </Tooltip>
              }
            />

            <Autocomplete
              label="School/Institution"
              placeholder="e.g. University of Technology"
              required
              value={newEducation.school}
              onChange={(value) => setNewEducation({ ...newEducation, school: value })}
              data={institutionsDictionary}
              limit={10}
              disabled={isLoadingInstitutions}
              rightSection={
                <Tooltip label="Choose from existing institutions or type a new one freely" position="top" multiline maw={220} withArrow>
                  <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                    <InformationCircleIcon style={{ width: rem(16) }} />
                  </ActionIcon>
                </Tooltip>
              }
            />

            <MonthYearInput
              label="From"
              monthValue={newEducation.fromMonth}
              yearValue={newEducation.fromYear}
              onMonthChange={(value) => setNewEducation({ ...newEducation, fromMonth: value })}
              onYearChange={(value) => setNewEducation({ ...newEducation, fromYear: value })}
              minYear={1950}
              maxYear={new Date().getFullYear()}
              showValidation={showAddValidation}
              required
            />

            <MonthYearInput
              label="To"
              monthValue={newEducation.toMonth}
              yearValue={newEducation.toYear}
              onMonthChange={(value) => setNewEducation({ ...newEducation, toMonth: value })}
              onYearChange={(value) => setNewEducation({ ...newEducation, toYear: value })}
              minYear={1950}
              maxYear={new Date().getFullYear()}
              showValidation={showAddValidation}
            />

            <Group justify="flex-end" gap="sm">
              {showAddValidation && (() => {
                const errors: string[] = [];
                if (!newEducation.degree) errors.push('Degree/Qualification is required');
                if (!newEducation.school) errors.push('School/Institution is required');
                if (!newEducation.fromMonth || !newEducation.fromYear) errors.push('Start date (month and year) is required');
                return errors.length > 0 ? (
                  <Alert color="red" w="100%" py="xs">
                    <List size="sm" spacing={2}>
                      {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                    </List>
                  </Alert>
                ) : null;
              })()}
              <Button
                variant="outline"
                leftSection={<XMarkIcon width={16} height={16} />}
                onClick={() => {
                  setIsAddFormOpen(false);
                  setShowAddValidation(false);
                }}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                leftSection={<CheckIcon width={16} height={16} />}
                onClick={handleAddEducation}
                loading={isSaving}
              >
                Add Education
              </Button>
            </Group>
          </Stack>
        </Box>
      </Collapse>

      {education.length === 0 && !isAddFormOpen && (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No education entries added yet. Click "Add Education" to add your educational background.
        </Text>
      )}

      {education.map((edu) => (
        <React.Fragment key={edu.id}>
          {editingEducationId === edu.id ? (
            <Box
              mb="md"
              p="md"
              style={{
                border: `2px solid ${theme.colors.blue[5]}`,
                borderRadius: theme.radius.sm,
                backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.blue[0]
              }}
            >
              <Text size="sm" c="blue" fw={500} mb="md">Editing: {edu.degree} at {edu.school}</Text>
              <Stack gap="md">
                <Autocomplete
                  label="Degree/Qualification"
                  placeholder="e.g. Bachelor of Science in Computer Science"
                  required
                  value={editEducation.degree}
                  onChange={(value) => setEditEducation({ ...editEducation, degree: value })}
                  data={degreesDictionary}
                  limit={10}
                  disabled={isLoadingDegrees}
                  rightSection={
                    <Tooltip label="Choose from existing degree types or type a new one freely" position="top" multiline maw={220} withArrow>
                      <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                        <InformationCircleIcon style={{ width: rem(16) }} />
                      </ActionIcon>
                    </Tooltip>
                  }
                />

                <Autocomplete
                  label="School/Institution"
                  placeholder="e.g. University of Technology"
                  required
                  value={editEducation.school}
                  onChange={(value) => setEditEducation({ ...editEducation, school: value })}
                  data={institutionsDictionary}
                  limit={10}
                  disabled={isLoadingInstitutions}
                  rightSection={
                    <Tooltip label="Choose from existing institutions or type a new one freely" position="top" multiline maw={220} withArrow>
                      <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                        <InformationCircleIcon style={{ width: rem(16) }} />
                      </ActionIcon>
                    </Tooltip>
                  }
                />

                <MonthYearInput
                  label="From"
                  monthValue={editEducation.fromMonth}
                  yearValue={editEducation.fromYear}
                  onMonthChange={(value) => setEditEducation({ ...editEducation, fromMonth: value })}
                  onYearChange={(value) => setEditEducation({ ...editEducation, fromYear: value })}
                  minYear={1950}
                  maxYear={new Date().getFullYear()}
                  showValidation={showEditValidation}
                  required
                />

                <MonthYearInput
                  label="To"
                  monthValue={editEducation.toMonth}
                  yearValue={editEducation.toYear}
                  onMonthChange={(value) => setEditEducation({ ...editEducation, toMonth: value })}
                  onYearChange={(value) => setEditEducation({ ...editEducation, toYear: value })}
                  minYear={1950}
                  maxYear={new Date().getFullYear()}
                  showValidation={showEditValidation}
                />

                <Group justify="flex-end" gap="sm">
                  {showEditValidation && (() => {
                    const errors: string[] = [];
                    if (!editEducation.degree) errors.push('Degree/Qualification is required');
                    if (!editEducation.school) errors.push('School/Institution is required');
                    if (!editEducation.fromMonth || !editEducation.fromYear) errors.push('Start date (month and year) is required');
                    return errors.length > 0 ? (
                      <Alert color="red" w="100%" py="xs">
                        <List size="sm" spacing={2}>
                          {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                        </List>
                      </Alert>
                    ) : null;
                  })()}
                  <Button
                    variant="outline"
                    leftSection={<XMarkIcon width={16} height={16} />}
                    onClick={() => {
                      setEditingEducationId(null);
                      setShowEditValidation(false);
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    leftSection={<CheckIcon width={16} height={16} />}
                    onClick={saveEducationEdit}
                    loading={isSaving}
                  >
                    Save
                  </Button>
                </Group>
              </Stack>
            </Box>
          ) : (
            <CVItemCard
              title={edu.degree}
              subtitle={edu.school}
              dates={formatDateRange(edu.fromDate, edu.toDate)}
              description={edu.description}
              onEdit={() => handleEditEducation(edu)}
              onDelete={() => openDeleteConfirmation(edu)}
              isEditMode={isEditMode}
            />
          )}
        </React.Fragment>
      ))}

      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemName={educationToDelete?.degree}
        isLoading={isSaving}
      />
    </CVSectionContainer>
  );
} 
